import { OTLayout } from "@/components/layout/ot-layout";
import { useState } from "react";

const JPPE_PRINCIPLES = [
  {
    principle: "Respect for pain",
    detail: "If an activity causes pain during or within 2 hours after, it should be modified or avoided. Pain is a signal — not something to push through in inflammatory arthritis.",
  },
  {
    principle: "Reduce effort and force on joints",
    detail: "Use larger, stronger joints to carry out tasks. Use body weight rather than muscle strength. Spread load over the largest possible surface area.",
  },
  {
    principle: "Avoid positions of deformity",
    detail: "Avoid sustained positions that stress ligaments in the direction of potential deformity (e.g. avoid ulnar-deviating forces at the MCPs, avoid prolonged wrist flexion in RA CTS).",
  },
  {
    principle: "Avoid sustained, static positions",
    detail: "Static muscle contraction increases joint load. Change position and take micro-breaks every 20–30 minutes during sustained tasks.",
  },
  {
    principle: "Balance activity and rest",
    detail: "Intersperse periods of activity with rest. Use pacing and planning to avoid boom-and-bust patterns.",
  },
  {
    principle: "Maintain muscle strength and joint mobility",
    detail: "Joint protection works alongside exercise — not instead of it. Evidence supports concurrent joint protection education and exercise for hand function (Hammond & Freeman, 2001).",
  },
  {
    principle: "Use assistive devices",
    detail: "AT and adapted equipment reduce joint loading during functional tasks. Prescribe based on activity analysis and individual need.",
  },
];

const CONDITIONS: Record<string, { adaptations: string[]; avoidances: string[]; education: string[] }> = {
  "Rheumatoid Arthritis": {
    adaptations: [
      "Use built-up, padded handles on cutlery and pens to reduce MCP loading",
      "Jar opener to avoid ulnar-deviating wrist twist forces",
      "Carry bags over forearm, not in fingers — distribute load across MCPs",
      "Electric tin opener, kettle tipper, lever taps",
      "Larger MCP-sparing grip: use both hands on kettle or jug",
    ],
    avoidances: [
      "Avoid sustained pinch and grip during active synovitis",
      "Avoid ulnar-deviating hand positions (wringing cloths, fine turning movements)",
      "Avoid leaning on inflamed joints or pushing up from chair with fist clenched",
      "Avoid prolonged wrist flexion (typing without wrist support)",
    ],
    education: [
      "JPPE programme (Hammond, 1994) — 8-session group or individual format",
      "Arthritis Research UK (Versus Arthritis) joint protection leaflets",
      "Evidence: JPPE significantly reduces hand pain and improves function at 4 years (Hammond et al., 2002)",
    ],
  },
  "Psoriatic Arthritis": {
    adaptations: [
      "Dip joint protection — avoid prolonged DIP grip which loads entheses",
      "Sausage digit management — ring guards for swollen fingers during activity",
      "Footwear adaptations for heel enthesitis — orthotic referral",
      "Built-up writing aids for DIP-predominant disease",
    ],
    avoidances: [
      "Avoid nail pressure during DIJ activity (dactylitis)",
      "Avoid prolonged standing on hard surfaces with plantar fasciitis / heel enthesitis",
      "Limit high-impact activity during enthesitis flare",
    ],
    education: [
      "Condition-specific pacing around work — particularly if manual occupation",
      "PSAPS (Psoriatic Arthritis and Self-Management) resources (PAPAA, NRAS)",
    ],
  },
  "Systemic Lupus Erythematosus": {
    adaptations: [
      "Photoprotection for all outdoor activity — UV-protective gloves, long sleeves, hat",
      "Cooling aids (cooling vests, fans) for heat-sensitive fatigue and post-exertional malaise",
      "Energy conservation strategies prioritised over grip/strength considerations",
      "Jaccoud's arthropathy — flexible joint protection as for RA",
    ],
    avoidances: [
      "Avoid UV exposure without protection — may trigger flare",
      "Avoid pushing through post-exertional malaise — use energy envelope framework",
      "Avoid overexertion on high-disease-activity days",
    ],
    education: [
      "SLE fatigue is a distinct entity — different from RA fatigue; cognitive and physical components",
      "LUPUS UK resources and self-management planning",
    ],
  },
  "Systemic Sclerosis (SSc)": {
    adaptations: [
      "Warmth preservation: heated gloves, hand warmers, layering for Raynaud's",
      "Soft grip aids over rigid handles — avoid pressure on digital tips",
      "Mouth stretching exercises to maintain oral aperture for eating aids",
      "Electric toothbrush, adapted cutlery for restricted hand opening",
    ],
    avoidances: [
      "Avoid cold exposure without protection",
      "Avoid tight grip that may occlude digital circulation",
      "Avoid prolonged use of vibrating tools",
    ],
    education: [
      "SRUK (Scleroderma & Raynaud's UK) resources",
      "Raynaud's management — pharmacological and non-pharmacological alongside OT",
    ],
  },
};

export default function JointProtection() {
  const [selectedCondition, setSelectedCondition] = useState("Rheumatoid Arthritis");
  const [printMode, setPrintMode] = useState(false);

  const currentCondition = CONDITIONS[selectedCondition];

  return (
    <OTLayout title="Joint Protection">
      <div className="space-y-8">

        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-sm text-amber-800">
          <strong>JPPE Evidence Base:</strong> The Joint Protection Programme for people with RA (Hammond & Freeman, 2001–2004) is the most well-evaluated joint protection education programme in rheumatology. It delivers sustained improvements in joint protection behaviour, pain, and function at 4 years.
        </div>

        {/* JPPE Principles */}
        <section>
          <h2 className="text-lg font-bold text-slate-900 mb-4">Core JPPE Principles</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {JPPE_PRINCIPLES.map((p, i) => (
              <div key={p.principle} className="bg-white rounded-2xl border border-slate-200 p-5">
                <div className="flex items-start gap-3">
                  <span className="h-7 w-7 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-sm font-bold shrink-0">
                    {i + 1}
                  </span>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">{p.principle}</h3>
                    <p className="text-sm text-slate-600">{p.detail}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Condition-Specific */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-slate-900">Condition-Specific Adaptations</h2>
            <button
              onClick={() => setPrintMode(!printMode)}
              className="text-sm text-amber-700 border border-amber-300 px-3 py-1.5 rounded-lg hover:bg-amber-50 transition-colors"
            >
              {printMode ? "Exit print view" : "Print patient summary"}
            </button>
          </div>

          <div className="flex flex-wrap gap-2 mb-6">
            {Object.keys(CONDITIONS).map((c) => (
              <button
                key={c}
                onClick={() => setSelectedCondition(c)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${selectedCondition === c ? "bg-amber-600 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-amber-300"}`}
              >
                {c}
              </button>
            ))}
          </div>

          {printMode && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-4 text-sm text-amber-800 print:block">
              <strong>Patient summary — {selectedCondition}</strong>
              <br />This summary was prepared by your occupational therapist. These joint protection principles are tailored to your condition to help protect your joints, reduce pain, and maintain your independence.
            </div>
          )}

          <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-6">
            <h3 className="font-bold text-slate-900 text-base">{selectedCondition}</h3>

            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Recommended Adaptations</h4>
              <ul className="space-y-2">
                {currentCondition.adaptations.map((a) => (
                  <li key={a} className="text-sm text-slate-700 flex items-start gap-2">
                    <span className="text-amber-500 mt-1 shrink-0">✓</span>{a}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Movements / Positions to Avoid</h4>
              <ul className="space-y-2">
                {currentCondition.avoidances.map((a) => (
                  <li key={a} className="text-sm text-slate-700 flex items-start gap-2">
                    <span className="text-red-400 mt-1 shrink-0">✕</span>{a}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Education & Resources</h4>
              <ul className="space-y-2">
                {currentCondition.education.map((e) => (
                  <li key={e} className="text-sm text-slate-700 flex items-start gap-2">
                    <span className="text-amber-500 mt-1 shrink-0">📖</span>{e}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Pacing & Ergonomics */}
        <section className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
          <h2 className="font-bold text-slate-900">Pacing, Posture & Ergonomics</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-amber-50 rounded-xl p-4">
              <h4 className="font-semibold text-amber-800 mb-2">Pacing</h4>
              <ul className="text-sm text-amber-900 space-y-1">
                <li>• Break tasks into smaller segments</li>
                <li>• Work to a consistent pace — not boom-and-bust</li>
                <li>• Use timer to take regular micro-breaks</li>
                <li>• Plan high-effort tasks for best-energy periods</li>
              </ul>
            </div>
            <div className="bg-orange-50 rounded-xl p-4">
              <h4 className="font-semibold text-orange-800 mb-2">Posture</h4>
              <ul className="text-sm text-orange-900 space-y-1">
                <li>• Work at correct height to avoid sustained wrist deviation</li>
                <li>• Support forearms during fine motor tasks</li>
                <li>• Avoid prolonged neck flexion</li>
                <li>• Adjust seating to support lumbar spine</li>
              </ul>
            </div>
            <div className="bg-yellow-50 rounded-xl p-4">
              <h4 className="font-semibold text-yellow-800 mb-2">Ergonomics</h4>
              <ul className="text-sm text-yellow-900 space-y-1">
                <li>• Position keyboard and mouse to minimise ulnar deviation</li>
                <li>• Use vertical mouse or trackball for wrist-neutral use</li>
                <li>• Voice-to-text for prolonged writing tasks</li>
                <li>• Workstation assessment referral if employed</li>
              </ul>
            </div>
          </div>
        </section>

      </div>
    </OTLayout>
  );
}
